import { H1, H3 } from '@/components'
import { Card, MonthPicker } from '@/components_deprecated'
import { Field } from '@/modules/companies'
import { format } from '@/utils'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import {
  endOfMonth,
  formatISO,
  isSameDay,
  parseISO,
  startOfMonth,
} from 'date-fns'
import React from 'react'
import styled from 'styled-components'
import { VEHICLE_ACTIVITIES_WITHOUT_CARD } from '../queries'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
  position: relative;
`

const InfoContentWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 1rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

const ActivityCard = styled(Card)`
  background-color: ${props => props.theme.colors.white};
  border: 1px solid ${props => props.theme.colors.gray8};
`

export const DrivingWithoutCard: React.FC = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    period: 'common.period',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    manualRegistrations: 'activities.manual-registrations',
    minutes: 'common.minutes',
    drivingWithoutCard: 'activities.driving-without-card',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',
    chooseGroup: 'common.choose-group',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    confirmHours: 'salary.confirm-hours',

    showResolved: 'salary.show-resolved',
    showOk: 'activities.show-ok',

    leaderGroups: 'companies.leader-groups',
    noManualRegistration: 'salary.no-manual-registration',
  })

  const form = useForm<{
    datetimeStart: Date
    datetimeEnd: Date
  }>({
    values: {
      datetimeStart: startOfMonth(new Date()),
      datetimeEnd: endOfMonth(new Date()),
    },
    config: {
      storage: {
        storeFormState: true,
        retrieveFormStateOnMount: true,
        storeFormStateName: 'driving-without-card-filters',
        transformValues: {
          datetimeStart: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          datetimeEnd: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { data } = useQuery(VEHICLE_ACTIVITIES_WITHOUT_CARD, {
    variables: {
      datetimeStart: formatISO(form.formValues.datetimeStart),
      datetimeEnd: formatISO(form.formValues.datetimeEnd),
    },
  })

  const activities = data?.vehicleActivitiesWithoutCard ?? []

  return (
    <Wrapper>
      <InfoContentWrapper>
        <H1>{translations.drivingWithoutCard} </H1>
      </InfoContentWrapper>
      <Controls>
        <section>
          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={form.formValues.datetimeStart}
              onChange={date => {
                const start = startOfMonth(date)
                const end = endOfMonth(date)
                form.updateForm({
                  datetimeStart: start,
                  datetimeEnd: end,
                })
              }}
            />
          </Field>
        </section>
      </Controls>

      <div className="flex flex-col gap-2">
        {activities.map(activity => {
          const startDate = parseISO(activity.datetimeStart)
          const endDate = parseISO(activity.datetimeEnd)
          const isSameDayValue = isSameDay(startDate, endDate)
          return (
            <ActivityCard key={activity.id}>
              <div>
                <H3>
                  {activity.vehicle?.registrationNumber ?? activity.vehicleVin}
                </H3>
                <div>
                  <strong>{translations.period}: </strong>{' '}
                  {isSameDayValue
                    ? format(startDate, 'dd.MM.yyyy HH:mm')
                    : format(startDate, 'dd.MM.yyyy HH:mm')}{' '}
                  -{' '}
                  {isSameDayValue
                    ? format(endDate, 'HH:mm')
                    : format(endDate, 'dd.MM.yyyy HH:mm')}
                </div>
                <div className="flex gap-1">
                  <strong>{translations.driving}:</strong>{' '}
                  {activity.duration ?? 0}
                </div>
              </div>
            </ActivityCard>
          )
        })}
      </div>
    </Wrapper>
  )
}
